import React from "react";
import { Controller } from "react-hook-form";
import Label from "./Label";

const Input = React.forwardRef(
  (
    {
      className,
      parentClassName,
      type = "text",
      label,
      control,
      rules,
      name,
      isPositiveNumber = false,
      isInteger = false,
      isCurrency = false,
      isTextarea = false,
      onChangeFn,
      blockUntilOnChangeIsTrue = false,
      disabled = false,
      hideLabel = false,
      ...props
    },
    ref
  ) => {
    // Prevent scroll wheel from changing number input values
    const handleWheel = React.useCallback(
      (e) => {
        if (type === "number") {
          e.target.blur();
        }
      },
      [type]
    );

    const onKeyDown = (e) => {
      if (type === "number") {
        const allowedChars = [
          "Backspace",
          "Delete",
          "ArrowLeft",
          "ArrowRight",
          "Tab",
        ];

        if (!isPositiveNumber) {
          allowedChars.push("-");

          if (e.key === "-") {
            if (e.target.value.includes("-") || e.target.selectionStart !== 0) {
              e.preventDefault();
            }
            return;
          }
        }

        if (!isInteger) {
          if (e.key === ".") {
            if (e.target.value.includes(".")) {
              e.preventDefault();
            }
            return;
          }
        }

        if (
          /^[0-9]$/.test(e.key) ||
          allowedChars.includes(e.key) ||
          (e.key === "v" && (e.ctrlKey || e.metaKey)) ||
          (e.key === "c" && (e.ctrlKey || e.metaKey)) ||
          (e.key === "a" && (e.ctrlKey || e.metaKey)) ||
          (e.key === "r" && (e.ctrlKey || e.metaKey))
        ) {
          return true;
        } else {
          e.preventDefault();
        }
      }
    };

    const validateInputOnChange = (e) => {
      const val = e.target.value;
      if (type === "number") {
        if (props.min && parseFloat(val) < props.min) {
          return false;
        }
        if (props.max && parseFloat(val) > props.max) {
          return false;
        }
      }
      return true;
    };

    const onTextPaste = (e) => {
      const clipboardData = e.clipboardData || window.clipboardData;
      const pastedData = e.target.value + clipboardData.getData("Text");

      if (type === "number") {
        let regex;
        if (isInteger) {
          regex = isPositiveNumber ? /^[0-9]+$/ : /^-?[0-9]+$/;
        } else {
          regex = isPositiveNumber
            ? /^[0-9]*\.?[0-9]*$/
            : /^-?[0-9]*\.?[0-9]*$/;
        }

        if (!regex.test(pastedData)) {
          e.preventDefault();
        }
      }
    };

    return (
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <div className={`flex flex-col space-y-2 ${parentClassName || ""}`}>
            {!hideLabel && label && (
              <Label>
                {label}
                {rules?.required && <span className="text-red-500">*</span>}
              </Label>
            )}
            <div className="flex items-center">
              {isCurrency && (
                <span className="inline-flex items-center px-4 py-3 border border-r-0 border-gray-300 text-gray-700 font-medium bg-white rounded-l">
                  ₹
                </span>
              )}
              {isTextarea ? (
                <textarea
                  ref={ref}
                  value={value || ""}
                  onChange={(e) => {
                    if (!disabled) {
                      if (blockUntilOnChangeIsTrue) {
                        if (onChangeFn?.(e)) {
                          onChange(e);
                        }
                      } else {
                        onChangeFn?.(e);
                        onChange(e);
                      }
                    }
                  }}
                  disabled={disabled}
                  rows="4"
                  className={`w-full px-4 py-3 border rounded focus:outline-none focus:ring-2 focus:ring-color1 resize-none ${error ? "border-red-500" : "border-gray-300"
                    } ${disabled ? "bg-gray-100 cursor-not-allowed" : ""} ${className || ""}`}
                  {...props}
                />
              ) : (
                <input
                  ref={ref}
                  type={type === "number" ? "text" : type}
                  value={value ?? ""}
                  onChange={(e) => {
                    if (!disabled && validateInputOnChange(e)) {
                      if (blockUntilOnChangeIsTrue) {
                        if (onChangeFn?.(e)) {
                          onChange(e);
                        }
                      } else {
                        onChangeFn?.(e);
                        onChange(e);
                      }
                    }
                  }}
                  onKeyDown={onKeyDown}
                  onPaste={onTextPaste}
                  onWheel={handleWheel}
                  disabled={disabled}
                  className={`w-full px-4 py-3 border rounded focus:outline-none focus:ring-2 focus:ring-color1 ${error ? "border-red-500" : "border-gray-300"
                    } ${disabled ? "bg-gray-100 cursor-not-allowed" : ""} ${isCurrency ? "rounded-l-none" : ""
                    } ${className || ""}`}
                  {...props}
                />
              )}
            </div>
            {error && (
              <p className="text-red-600 text-sm mt-1">{error.message}</p>
            )}
          </div>
        )}
      />
    );
  }
);

Input.displayName = "Input";

export default Input;
